import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { Layout } from '../components/Layout';
import { shiftSwapAPI, scheduleAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import {
  ArrowLeftRight,
  Loader2,
  Clock,
  Calendar,
  User,
  Check,
  X,
  Send,
  AlertCircle,
} from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';

export default function ShiftSwaps() {
  const { user } = useAuth();
  const { selectedLocationId, isAllLocations } = useLocation();
  const activeLocId = isAllLocations ? user?.restaurant_id : selectedLocationId;
  const [loading, setLoading] = useState(true);
  const [swaps, setSwaps] = useState([]);
  const [mySchedules, setMySchedules] = useState([]);
  const [activeTab, setActiveTab] = useState('incoming');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!activeLocId) {
      setLoading(false);
      return;
    }

    try {
      const [swapsRes, schedulesRes] = await Promise.all([
        shiftSwapAPI.getAll(),
        scheduleAPI.getAll(activeLocId, {
          start_date: new Date().toISOString().split('T')[0],
        }),
      ]);

      setSwaps(swapsRes.data);
      // Filter to only show my future schedules
      const myFutureSchedules = schedulesRes.data.filter(
        s => s.user_id === user.id && s.date >= new Date().toISOString().split('T')[0]
      );
      setMySchedules(myFutureSchedules);
    } catch (error) {
      console.error('Error fetching shift swaps:', error);
    } finally {
      setLoading(false);
    }
  }, [user, activeLocId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateSwap = async () => {
    if (!selectedSchedule) {
      toast.error('Please select a shift to swap');
      return;
    }

    setSubmitting(true);
    try {
      await shiftSwapAPI.create({
        schedule_id: selectedSchedule,
        target_user_id: null, // Open request
        reason: reason || null,
      });
      toast.success('Swap request created! Your coworkers will be notified.');
      setCreateDialogOpen(false);
      setSelectedSchedule('');
      setReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create swap request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespond = async (swapId, response) => {
    try {
      await shiftSwapAPI.respond(swapId, response);
      toast.success(response === 'accept' ? 'Shift swap accepted!' : 'Shift swap declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };

  const handleCancel = async (swapId) => {
    if (!confirm('Cancel this swap request?')) return;

    try {
      await shiftSwapAPI.cancel(swapId);
      toast.success('Swap request cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  // Filter swaps
  const incomingSwaps = swaps.filter(s => 
    s.status === 'pending' && 
    s.requester_id !== user?.id &&
    (s.target_user_id === user?.id || s.target_user_id === null)
  );

  const outgoingSwaps = swaps.filter(s => s.requester_id === user?.id);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Pending</span>;
      case 'accepted':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Accepted</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Rejected</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">Cancelled</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Shift Swaps
              <HelpTooltip text="Employees can request to swap shifts with coworkers. Managers review and approve swap requests before the schedule is updated." learnMoreUrl="/help/shift-swaps" />
            </h1>
            <p className="text-slate-500 mt-1">
              Request and manage shift swaps with coworkers
            </p>
          </div>

          <Button onClick={() => setCreateDialogOpen(true)} data-testid="request-swap-btn">
            <ArrowLeftRight className="mr-2 h-4 w-4" />
            Request Swap
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{incomingSwaps.length}</p>
                  <p className="text-sm text-slate-500">Incoming</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Send className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {outgoingSwaps.filter(s => s.status === 'pending').length}
                  </p>
                  <p className="text-sm text-slate-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Check className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {swaps.filter(s => s.status === 'accepted').length}
                  </p>
                  <p className="text-sm text-slate-500">Accepted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Calendar className="text-slate-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{mySchedules.length}</p>
                  <p className="text-sm text-slate-500">My Shifts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card className="border-slate-200">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <CardHeader className="pb-0">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="incoming" data-testid="tab-incoming">
                  Incoming Requests
                  {incomingSwaps.length > 0 && (
                    <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {incomingSwaps.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="outgoing" data-testid="tab-outgoing">
                  My Requests
                </TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Incoming Requests */}
              <TabsContent value="incoming">
                {incomingSwaps.length === 0 ? (
                  <div className="text-center py-8">
                    <ArrowLeftRight className="mx-auto h-12 w-12 text-slate-300" />
                    <p className="mt-2 text-slate-500">No incoming swap requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {incomingSwaps.map((swap) => (
                      <div
                        key={swap.id}
                        className="p-4 border border-slate-200 rounded-lg bg-amber-50"
                        data-testid={`swap-incoming-${swap.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <User size={16} className="text-slate-500" />
                              <span className="font-medium text-slate-900">{swap.requester_name}</span>
                              <span className="text-slate-400">wants to swap</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {formatDate(swap.schedule_date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={14} />
                                {swap.schedule_start_time} - {swap.schedule_end_time}
                              </span>
                            </div>
                            {swap.reason && (
                              <p className="mt-2 text-sm text-slate-500 italic">&ldquo;{swap.reason}&rdquo;</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRespond(swap.id, 'reject')}
                              className="border-red-200 text-red-600 hover:bg-red-50"
                              data-testid={`reject-swap-${swap.id}`}
                            >
                              <X className="mr-1 h-4 w-4" />
                              Decline
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleRespond(swap.id, 'accept')}
                              className="bg-green-500 hover:bg-green-600"
                              data-testid={`accept-swap-${swap.id}`}
                            >
                              <Check className="mr-1 h-4 w-4" />
                              Accept
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Outgoing Requests */}
              <TabsContent value="outgoing">
                {outgoingSwaps.length === 0 ? (
                  <div className="text-center py-8">
                    <Send className="mx-auto h-12 w-12 text-slate-300" />
                    <p className="mt-2 text-slate-500">You haven&apos;t made any swap requests</p>
                    <Button onClick={() => setCreateDialogOpen(true)} className="mt-4">
                      <ArrowLeftRight className="mr-2 h-4 w-4" />
                      Request a Swap
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {outgoingSwaps.map((swap) => (
                      <div
                        key={swap.id}
                        className={`p-4 border rounded-lg ${
                          swap.status === 'pending' ? 'border-blue-200 bg-blue-50' :
                          swap.status === 'accepted' ? 'border-green-200 bg-green-50' :
                          swap.status === 'rejected' ? 'border-red-200 bg-red-50' :
                          'border-slate-200 bg-slate-50'
                        }`}
                        data-testid={`swap-outgoing-${swap.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getStatusBadge(swap.status)}
                              {swap.target_user_name ? (
                                <span className="text-sm text-slate-600">
                                  {swap.status === 'accepted' ? 'Swapped with' : 'Requested from'} {swap.target_user_name}
                                </span>
                              ) : (
                                <span className="text-sm text-slate-600">Open request to all staff</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {formatDate(swap.schedule_date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={14} />
                                {swap.schedule_start_time} - {swap.schedule_end_time}
                              </span>
                            </div>
                            {swap.reason && (
                              <p className="mt-2 text-sm text-slate-500 italic">&ldquo;{swap.reason}&rdquo;</p>
                            )}
                          </div>
                          {swap.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancel(swap.id)}
                              className="border-slate-200"
                              data-testid={`cancel-swap-${swap.id}`}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        {/* Create Swap Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowLeftRight className="text-orange-500" size={24} />
                Request Shift Swap
              </DialogTitle>
              <DialogDescription>
                Select a shift you want to swap and other staff will be notified
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Select Shift to Swap</Label>
                {mySchedules.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">
                    You don&apos;t have any upcoming shifts to swap
                  </p>
                ) : (
                  <Select value={selectedSchedule} onValueChange={setSelectedSchedule}>
                    <SelectTrigger data-testid="select-shift-to-swap">
                      <SelectValue placeholder="Choose a shift" />
                    </SelectTrigger>
                    <SelectContent>
                      {mySchedules.map((schedule) => (
                        <SelectItem key={schedule.id} value={schedule.id}>
                          {formatDate(schedule.date)} • {schedule.start_time} - {schedule.end_time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>Reason (Optional)</Label>
                <Textarea
                  placeholder="Let your coworkers know why you need to swap..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="swap-reason-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateSwap}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  disabled={submitting || !selectedSchedule}
                  data-testid="submit-swap-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Request
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
